// src/pages/Production/ProductionOrderDetailNew.jsx

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, onSnapshot, collection, query, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import ProductionOrderActivity from '../../components/production/ProductionOrderActivity';
import ProductionStageTracker from '../../components/production/ProductionStageTracker';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#06C167',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        gap: '30px',
        alignItems: 'start'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    infoList: {
        listStyle: 'none',
        padding: 0
    },
    infoItem: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '16px 0',
        borderBottom: '1px solid #f1f5f9',
        alignItems: 'center'
    },
    infoLabel: {
        color: '#64748b',
        flexBasis: '40%',
        fontSize: '14px'
    },
    infoValue: {
        fontWeight: '500',
        color: '#0f172a',
        flexBasis: '60%',
        textAlign: 'right',
        fontSize: '14px'
    },
    statusBadge: {
        display: 'inline-block',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: '600'
    },
    plannedBadge: {
        backgroundColor: '#dbeafe',
        color: '#1e40af'
    },
    inProgressBadge: {
        backgroundColor: '#fef3c7',
        color: '#92400e'
    },
    completedBadge: {
        backgroundColor: '#dcfce7',
        color: '#166534'
    },
    cancelledBadge: {
        backgroundColor: '#fee2e2',
        color: '#991b1b'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: '20px'
    },
    th: {
        background: '#f8fafc',
        padding: '12px 15px',
        textAlign: 'left',
        borderBottom: '2px solid #e2e8f0',
        color: '#64748b',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    td: {
        padding: '15px',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'middle',
        fontSize: '14px'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    },
    modalContent: {
        background: 'white',
        padding: '30px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #e2e8f0',
        paddingBottom: '15px',
        marginBottom: '20px'
    },
    deleteButton: {
        background: 'none',
        border: 'none',
        color: '#dc3545',
        cursor: 'pointer',
        fontSize: '20px'
    },
    progressContainer: {
        width: '100%',
        height: '12px',
        background: '#e2e8f0',
        borderRadius: '6px',
        overflow: 'hidden',
        margin: '10px 0'
    },
    progressBar: {
        height: '100%',
        background: '#10b981',
        borderRadius: '6px'
    },
    stageCard: {
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '15px'
    },
    stageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
    },
    stageTitle: {
        fontSize: '16px',
        fontWeight: '600',
        margin: 0
    },
    stageStatusBadge: {
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500'
    },
    pendingBadge: {
        backgroundColor: '#dbeafe',
        color: '#1e40af'
    },
    inProgressStageBadge: {
        backgroundColor: '#fef3c7',
        color: '#92400e'
    },
    completedStageBadge: {
        backgroundColor: '#dcfce7',
        color: '#166534'
    },
    cancelledStageBadge: {
        backgroundColor: '#fee2e2',
        color: '#991b1b'
    },
    // New styles for improved UI
    summaryCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '30px'
    },
    summaryItem: {
        textAlign: 'center'
    },
    summaryValue: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a'
    },
    summaryLabel: {
        fontSize: '14px',
        color: '#64748b',
        marginTop: '4px'
    },
    priorityHigh: {
        color: '#dc2626',
        fontWeight: '600'
    },
    priorityNormal: {
        color: '#f59e0b',
        fontWeight: '600'
    },
    priorityLow: {
        color: '#10b981',
        fontWeight: '600'
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '600',
        margin: '0 0 20px 0',
        color: '#0f172a'
    },
    emptyState: {
        padding: '40px',
        textAlign: 'center',
        color: '#64748b',
        fontStyle: 'italic'
    },
    // Add new styles for printing functionality
    printButton: {
        padding: '8px 16px',
        background: '#f1f5f9',
        color: '#374151',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginLeft: '10px'
    },
    printIcon: {
        fontSize: '16px'
    }
};

function ProductionOrderDetailNew() {
    const { orderId } = useParams();
    const { userData } = useAuth();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [products, setProducts] = useState([]);

    // Add subcontractor data
    const [subcontractors, setSubcontractors] = useState([]);
    const [totalRawMaterials, setTotalRawMaterials] = useState([]);
    const [expandedRecipes, setExpandedRecipes] = useState({});

    const toggleRecipeExpansion = (recipeId) => {
        setExpandedRecipes(prev => ({
            ...prev,
            [recipeId]: prev[recipeId] === undefined ? false : !prev[recipeId]
        }));
    };

    useEffect(() => {
        if (!userData?.companyId || !orderId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        // Fetch the production order
        const orderDocRef = doc(db, 'companies', companyId, 'productionOrders', orderId);
        const unsubscribeOrder = onSnapshot(orderDocRef, async (orderDocSnap) => {
            if (orderDocSnap.exists()) {
                const orderData = { id: orderDocSnap.id, ...orderDocSnap.data() };
                setOrder(orderData);

                // DEBUG: Log the order data to see the structure
                console.log('Production Order Data:', orderData);
                if (orderData.stages) {
                    orderData.stages.forEach((stage, index) => {
                        console.log(`Stage ${index}:`, stage);
                        console.log(`Stage ${index} inputConsumed:`, stage.inputConsumed);
                        console.log(`Stage ${index} inputMaterials:`, stage.inputMaterials);
                    });
                }

                // Fetch raw materials
                const rawMaterialsQuery = query(collection(db, 'companies', companyId, 'rawMaterials'));
                const rawMaterialsSnap = await getDocs(rawMaterialsQuery);
                setRawMaterials(rawMaterialsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                // Fetch products
                const productsQuery = query(collection(db, 'companies', companyId, 'products'));
                const productsSnap = await getDocs(productsQuery);
                setProducts(productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                // Fetch subcontractors
                const subcontractorsQuery = query(collection(db, 'companies', companyId, 'subcontractors'));
                const subcontractorsSnap = await getDocs(subcontractorsQuery);
                setSubcontractors(subcontractorsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } else {
                console.log('No such order!');
            }
            setLoading(false);
        });

        return () => unsubscribeOrder();
    }, [userData, orderId]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'planned':
                return <span style={{ ...styles.statusBadge, ...styles.plannedBadge }}>Planlandı</span>;
            case 'inProgress':
                return <span style={{ ...styles.statusBadge, ...styles.inProgressBadge }}>Devam Ediyor</span>;
            case 'completed':
                return <span style={{ ...styles.statusBadge, ...styles.completedBadge }}>Tamamlandı</span>;
            case 'cancelled':
                return <span style={{ ...styles.statusBadge, ...styles.cancelledBadge }}>İptal Edildi</span>;
            default:
                return <span style={{ ...styles.statusBadge, backgroundColor: '#e2e8f0', color: '#64748b' }}>{status}</span>;
        }
    };

    const getStageStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <span style={{ ...styles.stageStatusBadge, ...styles.pendingBadge }}>Bekliyor</span>;
            case 'inProgress':
                return <span style={{ ...styles.stageStatusBadge, ...styles.inProgressStageBadge }}>Devam Ediyor</span>;
            case 'completed':
                return <span style={{ ...styles.stageStatusBadge, ...styles.completedStageBadge }}>Tamamlandı</span>;
            case 'cancelled':
                return <span style={{ ...styles.stageStatusBadge, ...styles.cancelledStageBadge }}>İptal Edildi</span>;
            default:
                return <span style={{ ...styles.stageStatusBadge, backgroundColor: '#e2e8f0', color: '#64748b' }}>{status}</span>;
        }
    };

    const getPriorityStyle = (priority) => {
        switch (priority) {
            case 'high':
                return styles.priorityHigh;
            case 'normal':
                return styles.priorityNormal;
            case 'low':
                return styles.priorityLow;
            default:
                return {};
        }
    };

    const getPriorityText = (priority) => {
        switch (priority) {
            case 'high':
                return 'Yüksek';
            case 'normal':
                return 'Normal';
            case 'low':
                return 'Düşük';
            default:
                return priority;
        }
    };

    const getProgressPercentage = () => {
        if (!order) return 0;
        return Math.round((order.completedQuantity / order.quantity) * 100);
    };

    const handleStageComplete = async (stageId, completionData) => {
        try {
            setOrder(prevOrder => {
                const updatedStages = prevOrder.stages.map(stage => {
                    if (stage.id === stageId) {
                        // Check if we're just updating the status or completing the stage
                        if (completionData.status) {
                            // This is a status update (e.g., pending -> inProgress)
                            return {
                                ...stage,
                                ...completionData
                            };
                        } else {
                            // This is a completion (inProgress -> completed or subcontractor update)
                            return {
                                ...stage,
                                ...completionData
                            };
                        }
                    }
                    return stage;
                });

                // Update overall order completion
                const completedStages = updatedStages.filter(s => s.status === 'completed').length;
                const totalStages = updatedStages.length;
                const newCompletedQuantity = Math.round((completedStages / totalStages) * prevOrder.quantity);

                return {
                    ...prevOrder,
                    stages: updatedStages,
                    completedQuantity: newCompletedQuantity,
                    status: completedStages === totalStages ? 'completed' :
                        completedStages > 0 ? 'inProgress' :
                            prevOrder.status === 'inProgress' ? 'inProgress' : 'planned'
                };
            });

            // Save the updated stage information to the database
            const orderRef = doc(db, 'companies', userData.companyId, 'productionOrders', orderId);
            const orderSnapshot = await getDocs(query(collection(db, 'companies', userData.companyId, 'productionOrders')));
            const orderDoc = orderSnapshot.docs.find(doc => doc.id === orderId);

            if (orderDoc) {
                const orderData = orderDoc.data();
                const updatedStages = orderData.stages.map(stage => {
                    if (stage.id === stageId) {
                        // Check if we're just updating the status or completing the stage
                        if (completionData.status) {
                            // This is a status update (e.g., pending -> inProgress)
                            return {
                                ...stage,
                                ...completionData
                            };
                        } else {
                            // This is a completion (inProgress -> completed or subcontractor update)
                            return {
                                ...stage,
                                ...completionData
                            };
                        }
                    }
                    return stage;
                });

                // Update overall order completion
                const completedStages = updatedStages.filter(s => s.status === 'completed').length;
                const totalStages = updatedStages.length;
                const newCompletedQuantity = Math.round((completedStages / totalStages) * orderData.quantity);

                await updateDoc(orderRef, {
                    stages: updatedStages,
                    completedQuantity: newCompletedQuantity,
                    status: completedStages === totalStages ? 'completed' :
                        completedStages > 0 ? 'inProgress' :
                            orderData.status === 'inProgress' ? 'inProgress' : 'planned'
                });
            }
        } catch (error) {
            console.error('Error updating stage:', error);
            alert('Aşama güncellenirken bir hata oluştu: ' + error.message);
        }
    };

    // Function to calculate total raw materials needed for the entire order
    const calculateTotalRawMaterials = () => {
        const materialTotals = {};

        if (order && order.stages) {
            order.stages.forEach((stage, stageIndex) => {
                // DEBUG: Log stage processing
                console.log(`Processing stage ${stageIndex}:`, stage.name);

                // Check for inputConsumed first (new format)
                if (stage.inputConsumed && Array.isArray(stage.inputConsumed)) {
                    console.log(`Stage ${stageIndex} has inputConsumed:`, stage.inputConsumed);
                    stage.inputConsumed.forEach((material, materialIndex) => {
                        // Skip if material is empty or invalid
                        if (!material || !material.material) {
                            console.log(`Skipping empty/invalid material at index ${materialIndex} in stage ${stageIndex}`);
                            return;
                        }

                        console.log(`Processing material in stage ${stageIndex}:`, material);
                        const key = `${material.material}-${material.unit}`;
                        if (!materialTotals[key]) {
                            materialTotals[key] = {
                                material: material.material,
                                unit: material.unit,
                                quantity: 0
                            };
                        }
                        // Ensure quantity is a valid number (already scaled during order creation)
                        const quantity = parseFloat(material.quantity) || 0;
                        materialTotals[key].quantity += quantity;
                    });
                }
                // Check for inputMaterials as fallback (old format)
                else if (stage.inputMaterials && Array.isArray(stage.inputMaterials)) {
                    console.log(`Stage ${stageIndex} has inputMaterials:`, stage.inputMaterials);
                    stage.inputMaterials.forEach((material, materialIndex) => {
                        // Skip if material is empty or invalid
                        if (!material || !material.material) {
                            console.log(`Skipping empty/invalid material at index ${materialIndex} in stage ${stageIndex}`);
                            return;
                        }

                        console.log(`Processing material in stage ${stageIndex}:`, material);
                        const key = `${material.material}-${material.unit}`;
                        if (!materialTotals[key]) {
                            materialTotals[key] = {
                                material: material.material,
                                unit: material.unit,
                                quantity: 0
                            };
                        }
                        // Ensure quantity is a valid number (already scaled during order creation)
                        const quantity = parseFloat(material.quantity) || 0;
                        materialTotals[key].quantity += quantity;
                    });
                } else {
                    console.log(`Stage ${stageIndex} has no input materials`);
                }
            });
        }

        console.log('Calculated material totals:', materialTotals);
        const result = Object.values(materialTotals);
        console.log('Returning raw materials result:', result);
        return result;
    };

    // Update the useEffect that calculates raw materials
    useEffect(() => {
        if (order) {
            console.log('Order data for raw materials calculation:', order);
            const materials = calculateTotalRawMaterials();
            setTotalRawMaterials(materials);
            console.log('Setting total raw materials:', materials);
        }
    }, [order]);

    // Function to generate subcontractor slip content
    const generateSubcontractorSlipContent = (stage, subcontractorInfo) => {
        // Determine product name for the slip
        let productName = order.product;
        if (order.products && order.products.length > 0) {
            // Find the product associated with this stage's recipe
            const stageProduct = order.products.find(p => p.recipeId === stage.recipeId);
            if (stageProduct) {
                productName = stageProduct.name;
            }
        }

        const slipContent = `
            <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
                <div style="text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px;">
                    <h1 style="color: #333; margin: 0;">FASON ÜRETİM TALİMATI</h1>
                    <p style="color: #666; margin: 5px 0;">${productName} - ${stage.name}</p>
                </div>
                
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
                    <div>
                        <h3 style="color: #333; border-bottom: 1px solid #eee; padding-bottom: 5px;">Üretim Emri Bilgileri</h3>
                        <p><strong>Emir No:</strong> #${order.id.substring(0, 8).toUpperCase()}</p>
                        <p><strong>Ürün:</strong> ${productName}</p>
                        <p><strong>Aşama:</strong> ${stage.name}</p>
                        <p><strong>Tarih:</strong> ${new Date().toLocaleDateString('tr-TR')}</p>
                    </div>
                    
                    <div>
                        <h3 style="color: #333; border-bottom: 1px solid #eee; padding-bottom: 5px;">Fason Bilgileri</h3>
                        <p><strong>Fason Firma:</strong> ${subcontractorInfo.name || subcontractorInfo.supplierName}</p>
                        <p><strong>İş Emri No:</strong> ${stage.id.substring(0, 8).toUpperCase()}</p>
                        <p><strong>Durum:</strong> ${stage.status === 'pending' ? 'Beklemede' : stage.status === 'inProgress' ? 'Devam Ediyor' : stage.status === 'completed' ? 'Tamamlandı' : 'İptal Edildi'}</p>
                    </div>
                </div>
                
                <div style="margin-bottom: 20px;">
                    <h3 style="color: #333; border-bottom: 1px solid #eee; padding-bottom: 5px;">Üretim Detayları</h3>
                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; background: #f8f9fa; padding: 15px; border-radius: 5px;">
                        <div>
                            <p style="margin: 5px 0;"><strong>Üretilecek Miktar:</strong></p>
                            <p style="font-size: 20px; font-weight: bold; color: #2d7dd2;">${stage.outputProduced.quantity} ${stage.outputProduced.unit}</p>
                        </div>
                        <div>
                            <p style="margin: 5px 0;"><strong>Tahmini Süre:</strong></p>
                            <p style="font-size: 20px; font-weight: bold; color: #2d7dd2;">${stage.duration || 0} dakika</p>
                        </div>
                        <div>
                            <p style="margin: 5px 0;"><strong>Atölye:</strong></p>
                            <p style="font-size: 20px; font-weight: bold; color: #2d7dd2;">${stage.workCenter || '-'}</p>
                        </div>
                    </div>
                </div>
                
                <div style="margin-bottom: 20px;">
                    <h3 style="color: #333; border-bottom: 1px solid #eee; padding-bottom: 5px;">Girdi Hammaddeleri</h3>
                    <table style="width: 100%; border-collapse: collapse;">
                        <thead>
                            <tr style="background: #f1f3f5;">
                                <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Malzeme</th>
                                <th style="border: 1px solid #ddd; padding: 10px; text-align: center;">Miktar</th>
                                <th style="border: 1px solid #ddd; padding: 10px; text-align: center;">Birim</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${stage.inputConsumed && stage.inputConsumed.length > 0 ?
                stage.inputConsumed.map(material => `
                                <tr>
                                    <td style="border: 1px solid #ddd; padding: 10px;">${material.material}</td>
                                    <td style="border: 1px solid #ddd; padding: 10px; text-align: center;">${material.quantity}</td>
                                    <td style="border: 1px solid #ddd; padding: 10px; text-align: center;">${material.unit}</td>
                                </tr>
                              `).join('') :
                `<tr><td colspan="3" style="border: 1px solid #ddd; padding: 10px; text-align: center;">Hammadde bilgisi bulunamadı</td></tr>`
            }
                        </tbody>
                    </table>
                </div>
                
                ${subcontractorInfo.laborCost ? `
                <div style="margin-bottom: 20px; background: #fff9db; padding: 15px; border-radius: 5px; border-left: 4px solid #ffc107;">
                    <h3 style="color: #333; margin-top: 0;">İşçilik Bilgileri</h3>
                    <p><strong>Birim İşçilik Ücreti:</strong> ${subcontractorInfo.laborCost.toFixed(2)} ₺/${stage.outputProduced.unit}</p>
                    <p><strong>Toplam İşçilik Ücreti:</strong> ${(subcontractorInfo.laborCost * stage.outputProduced.quantity).toFixed(2)} ₺</p>
                </div>
                ` : ''}
                
                <div style="margin-bottom: 20px;">
                    <h3 style="color: #333; border-bottom: 1px solid #eee; padding-bottom: 5px;">Açıklamalar</h3>
                    <div style="min-height: 80px; border: 1px solid #ddd; border-radius: 5px; padding: 15px; background: #fff;">
                        ${stage.description || 'Bu aşamaya özel açıklama bulunmamaktadır.'}
                    </div>
                </div>
                
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 30px;">
                    <div style="text-align: center;">
                        <p style="border-top: 1px solid #333; padding-top: 10px; margin-top: 40px;">Yetkili İmza</p>
                    </div>
                    <div style="text-align: center;">
                        <p style="border-top: 1px solid #333; padding-top: 10px; margin-top: 40px;">Tarih/Saat</p>
                    </div>
                </div>
                
                <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 12px;">
                    <p>Bu belge ${new Date().getFullYear()} yılında oluşturulmuştur. | Üretim Emri No: #${order.id.substring(0, 8).toUpperCase()}</p>
                </div>
            </div>
        `;
        return slipContent;
    };

    // Function to print subcontractor slip
    const printSubcontractorSlip = (stage, subcontractorInfo) => {
        const slipContent = generateSubcontractorSlipContent(stage, subcontractorInfo);

        const printWindow = window.open('', '_blank');

        if (!printWindow) {
            alert("Yazdırma penceresi açılamadı. Lütfen tarayıcınızın pop-up engelleyicisini kapatın ve tekrar deneyin.");
            return;
        }
        printWindow.document.write(`
            <html>
                <head>
                    <title>Fason Üretim Talimatı - ${stage.name}</title>
                    <style>
                        body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
                        @media print {
                            body { margin: 0; padding: 10px; }
                        }
                    </style>
                </head>
                <body>
                    ${slipContent}
                    <script>
                        window.onload = function() {
                            window.print();
                            // Close the window after printing (optional)
                            // window.close();
                        }
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    if (loading) return <p>Yükleniyor...</p>;
    if (!order) return <h2>Üretim Emri Bulunamadı</h2>;

    // DEBUG: Log the order data
    console.log('Rendering ProductionOrderDetailNew with order:', order);

    // DEBUG: Log the calculated raw materials
    console.log('Total raw materials calculated:', totalRawMaterials);

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Link to="/uretim/emirler" style={styles.backLink}>
                    ← Üretim Emirleri
                </Link>
                <div style={styles.headerActions}>
                    <button
                        onClick={() => alert('Üretim emri iptal edildi!')}
                        style={{ ...styles.button, background: '#ef4444' }}
                    >
                        İptal Et
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '30px' }}>
                <div style={styles.summaryCard}>
                    <div>
                        <div style={styles.summaryValue}>{order.totalQuantity || order.quantity}</div>
                        <div style={styles.summaryLabel}>Planlanan Miktar</div>
                    </div>
                </div>
                <div style={styles.summaryCard}>
                    <div>
                        <div style={styles.summaryValue}>{order.completedQuantity}</div>
                        <div style={styles.summaryLabel}>Tamamlanan</div>
                    </div>
                </div>
                <div style={styles.summaryCard}>
                    <div>
                        <div style={styles.summaryValue}>{getProgressPercentage()}%</div>
                        <div style={styles.summaryLabel}>İlerleme</div>
                    </div>
                </div>
                <div style={styles.summaryCard}>
                    <div>
                        <div style={{ ...styles.summaryValue, fontSize: '16px' }}>{getStatusBadge(order.status)}</div>
                        <div style={styles.summaryLabel}>Durum</div>
                    </div>
                </div>
            </div>

            <div style={styles.grid}>
                <div>
                    <div style={styles.box}>
                        <h2 style={styles.sectionTitle}>Üretim Emri Detayları</h2>
                        <ul style={styles.infoList}>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Emir No</span>
                                <span style={styles.infoValue}>#{order.id.substring(0, 8).toUpperCase()}</span>
                            </li>
                            {order.customerName && (
                                <li style={styles.infoItem}>
                                    <span style={styles.infoLabel}>Müşteri / Firma</span>
                                    <span style={styles.infoValue}>{order.customerName}</span>
                                </li>
                            )}
                            {order.referenceNumber && (
                                <li style={styles.infoItem}>
                                    <span style={styles.infoLabel}>Sipariş / Ref. No</span>
                                    <span style={styles.infoValue}>{order.referenceNumber}</span>
                                </li>
                            )}
                            {/* Show products section for multiple products or single product section */}
                            {order.products && order.products.length > 0 ? (
                                <li style={styles.infoItem}>
                                    <span style={styles.infoLabel}>Ürünler</span>
                                    <span style={styles.infoValue}>
                                        {order.products.map((product, index) => (
                                            <div key={index} style={{ marginBottom: '5px' }}>
                                                {product.name} ({product.quantity} adet)
                                            </div>
                                        ))}
                                    </span>
                                </li>
                            ) : (
                                <>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Ürün</span>
                                        <span style={styles.infoValue}>{order.product}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Reçete</span>
                                        <span style={styles.infoValue}>{order.recipeName}</span>
                                    </li>
                                </>
                            )}
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Öncelik</span>
                                <span style={{ ...styles.infoValue, ...getPriorityStyle(order.priority) }}>
                                    {getPriorityText(order.priority)}
                                </span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Başlangıç Tarihi</span>
                                <span style={styles.infoValue}>
                                    {order.startDate?.toDate ? order.startDate.toDate().toLocaleDateString('tr-TR') :
                                        order.startDate instanceof Date ? order.startDate.toLocaleDateString('tr-TR') :
                                            'Tarih yok'}
                                </span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Bitiş Tarihi</span>
                                <span style={styles.infoValue}>
                                    {order.endDate?.toDate ? order.endDate.toDate().toLocaleDateString('tr-TR') :
                                        order.endDate instanceof Date ? order.endDate.toLocaleDateString('tr-TR') :
                                            'Tarih yok'}
                                </span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Oluşturulma</span>
                                <span style={styles.infoValue}>
                                    {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString('tr-TR') :
                                        order.createdAt instanceof Date ? order.createdAt.toLocaleDateString('tr-TR') :
                                            'Tarih yok'}
                                </span>
                            </li>
                        </ul>
                    </div>

                    {/* New section for raw materials */}
                    <div style={{ ...styles.box, marginTop: '30px' }}>
                        <h3 style={{ ...styles.sectionTitle, fontSize: '18px', marginBottom: '15px' }}>Kullanılacak Hammaddeler</h3>

                        {/* DEBUG: Show raw data for debugging */}
                        <div style={{ marginBottom: '10px', padding: '10px', background: '#f0f0f0', borderRadius: '4px', fontSize: '12px' }}>
                            <strong>DEBUG:</strong> Total raw materials count: {totalRawMaterials.length}
                        </div>

                        {totalRawMaterials.length > 0 ? (
                            <ul style={{ ...styles.infoList, padding: 0 }}>
                                {totalRawMaterials.map((material, index) => (
                                    <li
                                        key={`${material.material}-${material.unit}`}
                                        style={{
                                            ...styles.infoItem,
                                            padding: '12px 0',
                                            borderBottom: index < totalRawMaterials.length - 1 ? '1px solid #f1f5f9' : 'none'
                                        }}
                                    >
                                        <span style={{ ...styles.infoLabel, flexBasis: '60%' }}>{material.material}</span>
                                        <span style={{ ...styles.infoValue, flexBasis: '40%', textAlign: 'right' }}>
                                            {parseFloat(material.quantity).toFixed(2)} {material.unit}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p style={{ color: '#64748b', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                                Bu üretim emri için hammadde bilgisi bulunmamaktadır.
                            </p>
                        )}
                    </div>
                </div>

                <div>
                    <div style={styles.box}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ ...styles.sectionTitle, margin: 0 }}>Üretim Aşamaları</h3>
                        </div>

                        {order.stages && order.stages.length > 0 ? (
                            Object.values(order.stages.reduce((acc, stage) => {
                                const key = stage.recipeId || 'default';
                                if (!acc[key]) {
                                    acc[key] = {
                                        recipeId: stage.recipeId,
                                        recipeName: stage.recipeName || 'Diğer İşlemler',
                                        stages: [],
                                        totalDuration: 0,
                                        completedStages: 0
                                    };
                                }
                                acc[key].stages.push(stage);
                                acc[key].totalDuration += (parseFloat(stage.duration) || 0);
                                if (stage.status === 'completed') {
                                    acc[key].completedStages += 1;
                                }
                                return acc;
                            }, {})).map((group, groupIndex) => {
                                // Calculate progress for this recipe group
                                const groupProgress = Math.round((group.completedStages / group.stages.length) * 100);
                                const isExpanded = expandedRecipes[group.recipeId] !== false; // Default to open

                                return (
                                    <div key={group.recipeId || groupIndex} style={{ marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                                        {/* Group Header - Click to toggle */}
                                        <div
                                            onClick={() => toggleRecipeExpansion(group.recipeId)}
                                            style={{
                                                background: '#f8fafc',
                                                padding: '15px 20px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <div style={{
                                                    width: '24px',
                                                    height: '24px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: '#64748b',
                                                    fontSize: '18px',
                                                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s'
                                                }}>
                                                    ▶
                                                </div>
                                                <div>
                                                    <h4 style={{ margin: 0, fontSize: '16px', color: '#0f172a' }}>{group.recipeName}</h4>
                                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                                                        {group.completedStages} / {group.stages.length} aşama tamamlandı • Toplam {group.totalDuration} dk
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                {/* Progress Bar for Group */}
                                                <div style={{ width: '100px', height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                                                    <div style={{ width: `${groupProgress}%`, height: '100%', background: '#06C167', borderRadius: '3px', transition: 'width 0.3s' }} />
                                                </div>
                                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#06C167', minWidth: '40px', textAlign: 'right' }}>
                                                    %{groupProgress}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Stages List - Collapsible */}
                                        {isExpanded && (
                                            <div style={{ padding: '20px' }}>
                                                {group.stages.map((stage) => {
                                                    // Find subcontractors logic (kept from original)
                                                    let stageSubcontractors = [];
                                                    if (stage.subcontractor) {
                                                        const subcontractor = subcontractors.find(s => s.id === stage.subcontractor.supplierId);
                                                        if (subcontractor) stageSubcontractors = [subcontractor];
                                                    } else if (stage.subcontractorDistribution) {
                                                        stageSubcontractors = stage.subcontractorDistribution.map(dist => {
                                                            const subcontractor = subcontractors.find(s => s.id === dist.supplierId);
                                                            return subcontractor ? { ...subcontractor, distribution: dist, laborCost: dist.laborCost || 0 } : null;
                                                        }).filter(Boolean);
                                                    } else if (stage.subcontractors) {
                                                        stageSubcontractors = stage.subcontractors.map(sub => {
                                                            const subcontractor = subcontractors.find(s => s.id === sub.supplierId);
                                                            return subcontractor ? { ...subcontractor, laborCost: sub.laborCost } : null;
                                                        }).filter(Boolean);
                                                    }

                                                    return (
                                                        <div key={stage.id} style={styles.stageCard}>
                                                            <div style={styles.stageHeader}>
                                                                <h4 style={styles.stageTitle}>
                                                                    {stage.sequence}. {stage.name}
                                                                    {stage.type === 'subcontracting' && (
                                                                        <span style={{
                                                                            marginLeft: '8px',
                                                                            fontSize: '12px',
                                                                            color: '#06C167',
                                                                            fontWeight: 'normal'
                                                                        }}>
                                                                            (Fason)
                                                                        </span>
                                                                    )}
                                                                </h4>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    {getStageStatusBadge(stage.status)}
                                                                    {stage.type === 'subcontracting' && stageSubcontractors.length > 0 && (
                                                                        <button
                                                                            onClick={() => printSubcontractorSlip(stage, stageSubcontractors[0])}
                                                                            style={styles.printButton}
                                                                            title="Fason Fişi Yazdır"
                                                                        >
                                                                            <span style={styles.printIcon}>🖨️</span>
                                                                            Yazdır
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                                                                <div>
                                                                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Atölye</div>
                                                                    <div style={{ fontWeight: '500' }}>{stage.workCenter || '-'}</div>
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Tahmini Süre</div>
                                                                    <div style={{ fontWeight: '500' }}>{stage.duration || 0} dakika</div>
                                                                </div>
                                                            </div>

                                                            <div style={{ marginBottom: '15px' }}>
                                                                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Çıktı Ürünü</div>
                                                                <div style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    background: 'white',
                                                                    border: '1px solid #e2e8f0',
                                                                    borderRadius: '8px',
                                                                    padding: '10px'
                                                                }}>
                                                                    <span>{stage.outputProduced.product}</span>
                                                                    <span>{stage.outputProduced.quantity} {stage.outputProduced.unit}</span>
                                                                </div>
                                                            </div>

                                                            {stage.type === 'subcontracting' && stageSubcontractors.length > 0 && (
                                                                <div style={{
                                                                    background: '#e2e8f0',
                                                                    padding: '15px',
                                                                    borderRadius: '8px',
                                                                    marginTop: '15px'
                                                                }}>
                                                                    <h4 style={{
                                                                        margin: '0 0 10px 0',
                                                                        fontSize: '14px',
                                                                        fontWeight: '600',
                                                                        color: '#374151'
                                                                    }}>
                                                                        Fason Tedarikçiler
                                                                    </h4>

                                                                    {stage.subcontractorDistribution && stage.subcontractorDistribution.length > 0 ? (
                                                                        <div>
                                                                            {stage.subcontractorDistribution.map((dist, distIndex) => {
                                                                                const subcontractor = subcontractors.find(s => s.id === dist.supplierId);
                                                                                return (
                                                                                    <div key={dist.supplierId} style={{
                                                                                        display: 'flex',
                                                                                        justifyContent: 'space-between',
                                                                                        padding: '8px 0',
                                                                                        borderBottom: distIndex < stage.subcontractorDistribution.length - 1 ? '1px solid #cbd5e1' : 'none'
                                                                                    }}>
                                                                                        <div style={{ fontWeight: '500' }}>
                                                                                            {subcontractor ? subcontractor.name : dist.supplierName}
                                                                                        </div>
                                                                                        <div>
                                                                                            {dist.completedQuantity || 0} / {dist.quantity} {stage.outputProduced.unit}
                                                                                            {dist.status === 'completed' && (
                                                                                                <span style={{
                                                                                                    background: '#10b981',
                                                                                                    color: 'white',
                                                                                                    padding: '2px 6px',
                                                                                                    borderRadius: '4px',
                                                                                                    fontSize: '10px',
                                                                                                    marginLeft: '8px'
                                                                                                }}>
                                                                                                    Tamamlandı
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    ) : (
                                                                        <div>
                                                                            {stageSubcontractors.map((subcontractor, subIndex) => (
                                                                                <div key={subcontractor.id} style={{
                                                                                    display: 'flex',
                                                                                    justifyContent: 'space-between',
                                                                                    padding: '8px 0',
                                                                                    borderBottom: subIndex < stageSubcontractors.length - 1 ? '1px solid #cbd5e1' : 'none'
                                                                                }}>
                                                                                    <div style={{ fontWeight: '500' }}>
                                                                                        {subcontractor.name}
                                                                                    </div>
                                                                                    <div>
                                                                                        {stage.outputProduced.quantity} {stage.outputProduced.unit}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            <ProductionStageTracker
                                                                key={stage.id}
                                                                stage={stage}
                                                                companyId={userData?.companyId}
                                                                orderId={orderId}
                                                                user={userData}
                                                                onComplete={handleStageComplete}
                                                                rawMaterials={rawMaterials}
                                                                products={products}
                                                                subcontractors={stageSubcontractors}
                                                                productionOrder={order}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div style={styles.emptyState}>
                                Bu üretim emrinde henüz aşama tanımlanmamış.
                            </div>
                        )}
                    </div>

                    <div style={{ marginTop: '30px' }}>
                        <ProductionOrderActivity companyId={userData?.companyId} orderId={orderId} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProductionOrderDetailNew;